import { useEffect, useState } from "react";

interface TokenInfo {
  id: string;
  label: string;
  prefix: string;
  allowed_origin: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface CreatedToken extends TokenInfo {
  token: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function CopyButton({
  text,
  label = "Copy",
}: {
  text: string;
  label?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API can be unavailable (insecure context); the fields
      // remain selectable so manual copy still works.
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={copy}
      className="px-2.5 py-1 text-[12px] font-medium rounded-lg border border-slate-200 bg-white hover:border-[#005bbb]/40 hover:text-[#005bbb] text-slate-600 transition-colors shrink-0"
    >
      {copied ? "Copied" : label}
    </button>
  );
}

function TokenRow({
  token,
  onRevoke,
}: {
  token: TokenInfo;
  onRevoke: (t: TokenInfo) => void;
}) {
  return (
    <div className="flex items-start gap-4 px-4 py-3 border-t border-slate-100 first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-medium text-slate-800">
            {token.label}
          </span>
          <code className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5">
            {token.prefix}…
          </code>
          <span
            className={
              token.allowed_origin
                ? "text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-full px-2 py-0.5"
                : "text-[11px] text-amber-700 bg-amber-50 border border-amber-200/70 rounded-full px-2 py-0.5"
            }
          >
            {token.allowed_origin ?? "any site"}
          </span>
        </div>
        <div className="text-[12px] text-slate-400 mt-1">
          Created {formatDate(token.created_at)} · Last used{" "}
          {formatDate(token.last_used_at)}
        </div>
      </div>
      <button
        onClick={() => onRevoke(token)}
        className="px-2.5 py-1 text-[12px] font-medium rounded-lg border border-red-100 text-red-600 hover:bg-red-50 transition-colors shrink-0"
      >
        Revoke
      </button>
    </div>
  );
}

// --- Crawler status ---

/** Status response from GET /api/admin/crawl. */
interface CrawlStatus {
  enabled: boolean;
  schedule: string;
  next_run_secs: number;
  index_dir: string | null;
  pages_indexed: number;
  last_run: {
    finished_at: string;
    trigger: string;
    ok: boolean;
    error?: string | null;
    duration_secs: number;
    fetched: number;
    failed: number;
    fail_status?: Record<string, number>;
    empty_body: number;
    new_pages: number;
    pages_indexed: number;
  } | null;
}

function fmtCountdown(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function failSummary(run: NonNullable<CrawlStatus["last_run"]>): string {
  const parts = Object.entries(run.fail_status ?? {}).map(
    ([code, n]) => `${n}× ${code === "0" ? "network" : `HTTP ${code}`}`,
  );
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

/** Last-run / next-run card for the nightly index refresh. */
function CrawlCard() {
  const [status, setStatus] = useState<CrawlStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await fetch("/api/admin/crawl");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus(await res.json());
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load crawler status",
      );
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reindex = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/admin/reindex", { method: "POST" });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reindex failed");
    } finally {
      setBusy(false);
    }
  };

  const run = status?.last_run ?? null;

  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/[0.06] border border-slate-200/70 p-5">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-[14px] font-semibold text-slate-900">
          Search index
        </h2>
        <button
          onClick={reindex}
          disabled={busy}
          className="text-[12.5px] font-medium text-[#005bbb] hover:bg-blue-50 rounded-lg px-2.5 py-1 transition-colors disabled:opacity-40"
        >
          {busy ? "Refreshing…" : "Refresh now"}
        </button>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-3">
        The assistant answers from a crawled snapshot of the CTSI site. A
        nightly job re-crawls and swaps in a fresh index — no restart needed.
      </p>
      {error && (
        <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5 mb-3">
          {error}
        </div>
      )}
      {status === null ? (
        <div className="text-[13px] text-slate-400">Loading…</div>
      ) : (
        <dl className="text-[13px] space-y-1.5">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Pages indexed</dt>
            <dd className="font-medium text-slate-800">
              {status.pages_indexed}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">Nightly refresh</dt>
            <dd className="font-medium text-slate-800">
              {status.enabled
                ? `daily at ${status.schedule} (next in ${fmtCountdown(status.next_run_secs)})`
                : "disabled"}
            </dd>
          </div>
          {run && (
            <>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Last run</dt>
                <dd
                  className={`font-medium ${run.ok ? "text-slate-800" : "text-red-600"}`}
                >
                  {formatDate(run.finished_at)} · {run.trigger}
                </dd>
              </div>
              {run.ok ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">Last result</dt>
                  <dd className="text-right text-slate-800">
                    {run.fetched} fetched, {run.failed} failed
                    {failSummary(run)}, {run.pages_indexed} indexed
                    <span className="text-slate-400">
                      {" "}
                      · {Math.round(run.duration_secs)}s
                    </span>
                  </dd>
                </div>
              ) : (
                <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                  {run.error ?? "Refresh failed"}
                </div>
              )}
            </>
          )}
          {!status.enabled && (
            <div className="text-[12.5px] text-slate-400">
              Automatic refresh is off (CRAWL_ENABLED=0) — use “Refresh now” to
              update the index manually.
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

export function AdminPage() {
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null);
  const [label, setLabel] = useState("");
  const [origin, setOrigin] = useState("");
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const embedOrigin = window.location.origin;

  const load = async () => {
    try {
      const res = await fetch("/api/admin/embed-tokens");
      if (res.status === 401) {
        window.location.reload();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTokens(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load tokens");
      setTokens([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch("/api/admin/embed-tokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: label.trim(),
          allowed_origin: origin.trim() || null,
        }),
      });
      if (res.status === 401) {
        window.location.reload();
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data: CreatedToken = await res.json();
      setCreated(data);
      setLabel("");
      setOrigin("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create token");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token: TokenInfo) => {
    if (
      !window.confirm(
        `Revoke "${token.label}"? The site embedding with ${token.prefix}… will stop working immediately.`,
      )
    )
      return;
    try {
      const res = await fetch(`/api/admin/embed-tokens/${token.id}`, {
        method: "DELETE",
      });
      if (res.status === 401) {
        window.location.reload();
        return;
      }
      if (!res.ok && res.status !== 404) {
        throw new Error(`HTTP ${res.status}`);
      }
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to revoke token");
    }
  };

  const snippet = created
    ? `<iframe\n  src="${embedOrigin}/?embed_token=${created.token}"\n  style="width:100%;height:640px;border:0"\n  title="CTSI Assistant"\n></iframe>`
    : "";

  return (
    <div className="min-h-screen bg-[#f7f8fa]">
      <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200/70 px-4 py-2.5 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-[15px] font-semibold text-slate-900 tracking-tight">
            Embed tokens
          </h1>
          <a
            href="/"
            className="text-[13px] text-[#005bbb]/80 hover:text-[#005bbb]"
          >
            ← Back to chat
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <CrawlCard />

        {created && (
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/[0.06] border border-emerald-200/70 p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <h2 className="text-[14px] font-semibold text-slate-900">
                Token created — copy it now
              </h2>
            </div>
            <p className="text-[12.5px] text-slate-500 mb-3">
              The full token is shown only once. Only its hash is stored, so a
              lost token must be revoked and reissued.
            </p>
            <div className="flex items-center gap-2 mb-3">
              <code className="flex-1 min-w-0 truncate text-[12.5px] bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-slate-800">
                {created.token}
              </code>
              <CopyButton text={created.token} label="Copy token" />
            </div>
            <p className="text-[12px] font-medium text-slate-500 mb-1.5">
              Then paste this into the CTSI page HTML:
            </p>
            <div className="flex items-start gap-2">
              <pre className="flex-1 min-w-0 overflow-x-auto text-[12px] bg-slate-900 text-slate-100 rounded-xl px-3 py-2.5">
                {snippet}
              </pre>
              <CopyButton text={snippet} label="Copy HTML" />
            </div>
          </div>
        )}

        <form
          onSubmit={generate}
          className="bg-white rounded-2xl shadow-xl shadow-slate-900/[0.06] border border-slate-200/70 p-5"
        >
          <h2 className="text-[14px] font-semibold text-slate-900 mb-1">
            New token
          </h2>
          <p className="text-[12.5px] text-slate-500 mb-3.5">
            Pin the token to the CTSI site origin so a copied iframe URL
            can&apos;t be re-embedded elsewhere. The host page must send a
            referrer (the browser default) for pinned tokens to work.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1.5 ml-1">
                Label
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="CTSI home page"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#005bbb]/[0.08] focus:border-[#005bbb]/50 focus:bg-white text-[15px] text-slate-800 placeholder:text-slate-400 transition-all"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1.5 ml-1">
                Allowed site origin{" "}
                <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                type="text"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder="https://www.buffalo.edu"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#005bbb]/[0.08] focus:border-[#005bbb]/50 focus:bg-white text-[15px] text-slate-800 placeholder:text-slate-400 transition-all"
              />
            </div>
            {error && (
              <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !label.trim()}
              className="w-full py-2.5 bg-[#005bbb] hover:bg-[#004a96] text-white text-[15px] font-medium rounded-xl shadow-sm shadow-blue-900/20 transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Generating…" : "Generate token"}
            </button>
          </div>
        </form>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/[0.06] border border-slate-200/70 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-[14px] font-semibold text-slate-900">
              Active tokens
            </h2>
          </div>
          {tokens === null ? (
            <div className="px-4 py-6 text-[13px] text-slate-400">Loading…</div>
          ) : tokens.length === 0 ? (
            <div className="px-4 py-6 text-[13px] text-slate-400">
              No tokens yet. Generate one above to embed the assistant on the
              CTSI site.
            </div>
          ) : (
            tokens.map((t) => (
              <TokenRow key={t.id} token={t} onRevoke={revoke} />
            ))
          )}
        </div>
      </main>
    </div>
  );
}
